import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Award, Search, Power } from "lucide-react";

/**
 * Admin · Plan Benefits Catalogue
 * Manage NPC benefits attached to membership plans (free consultations,
 * pathology discounts, vaccination benefits, health checkups, etc.).
 * Roles: super_admin + admin only.
 */

const EMPTY = {
  title: "",
  description: "",
  category: "Consultation",
  benefit_type: "free",
  usage_limit: 1,
  reset_period: "annual",
  discount_type: "none",
  discount_value: 0,
  department_applicability: [],
  plan_ids: [],
  active: true,
};

const TYPE_LABEL = { free: "Free", discount: "Discount", service: "Service" };
const PERIOD_LABEL = {
  one_time: "One-time (lifetime)",
  monthly: "Monthly reset",
  quarterly: "Quarterly reset",
  annual: "Annual reset",
};

export default function AdminBenefits() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null=closed, EMPTY/{}=editing
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p, c] = await Promise.all([
        api.get("/plan-benefits"),
        api.get("/plans?all=true"),
        api.get("/visits/meta/catalog"),
      ]);
      setRows(b.data || []);
      setPlans(p.data || []);
      setCatalog(c.data || {});
    } catch (e) {
      toast.error(apiErr(e, "Failed to load benefits"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) =>
      (b.title || "").toLowerCase().includes(q) ||
      (b.category || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const save = async () => {
    if (!editing) return;
    if (!(editing.title || "").trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        usage_limit: editing.usage_limit === "" || editing.usage_limit == null
          ? null
          : Number(editing.usage_limit),
        discount_value: Number(editing.discount_value || 0),
      };
      if (editing.benefit_id) {
        await api.put(`/plan-benefits/${editing.benefit_id}`, payload);
        toast.success("Benefit updated");
      } else {
        await api.post("/plan-benefits", payload);
        toast.success("Benefit created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete benefit "${b.title}"?`)) return;
    try {
      await api.delete(`/plan-benefits/${b.benefit_id}`);
      toast.success("Benefit deleted");
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Delete failed"));
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/plan-benefits/${b.benefit_id}`, { ...b, active: !b.active });
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Failed to toggle"));
    }
  };

  return (
    <div data-testid="admin-benefits-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F2540] flex items-center gap-2">
            <Award className="h-6 w-6 text-[#C89B3C]" />
            Plan Benefits
          </h1>
          <p className="text-sm text-[#5C6B7A] mt-1">
            Configure NPC benefits — free consultations, lab/radiology discounts,
            annual health checkups — and attach them to membership plans.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="benefits-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E4F87] text-white rounded-xl text-sm font-semibold hover:bg-[#0F2540]"
        >
          <Plus className="h-4 w-4" /> New benefit
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-[#E1E8F0] p-4">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
          <input
            type="text"
            placeholder="Search by title or category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="benefits-search"
            className="w-full pl-9 pr-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#E1E8F0] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#5C6B7A]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="benefits-empty">
            <Award className="h-10 w-10 text-[#C89B3C] opacity-50 mx-auto mb-3" />
            <p className="text-sm text-[#5C6B7A]">No benefits configured yet. Click &quot;New benefit&quot; to start.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FC] text-[#5C6B7A] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Usage</th>
                <th className="text-left px-4 py-3">Reset</th>
                <th className="text-left px-4 py-3">Plans</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.benefit_id} className="border-t border-[#E1E8F0] hover:bg-[#FAFBFD]" data-testid={`benefit-row-${b.benefit_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0F2540]">{b.title}</div>
                    {b.description && <div className="text-xs text-[#5C6B7A] mt-0.5">{b.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#0F2540]">{b.category}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#EAF1FA] text-[#1E4F87] font-medium">{TYPE_LABEL[b.benefit_type] || b.benefit_type}</span>
                    {b.benefit_type === "discount" && (
                      <span className="ml-2 text-xs text-[#5C6B7A]">
                        {b.discount_type === "percent" ? `${b.discount_value}%` : `₹${b.discount_value}`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#0F2540]">{b.usage_limit == null ? "Unlimited" : b.usage_limit}</td>
                  <td className="px-4 py-3 text-[#5C6B7A]">{PERIOD_LABEL[b.reset_period] || b.reset_period}</td>
                  <td className="px-4 py-3 text-[#5C6B7A]">
                    {(b.plan_ids || []).length === 0 ? (
                      <span className="text-[#A53A2A] text-xs">Unassigned</span>
                    ) : (
                      <span className="text-xs">{(b.plan_ids || []).length} plan(s)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(b)}
                      data-testid={`benefit-toggle-${b.benefit_id}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${b.active ? "bg-[#E5F2EA] text-[#1F7A3D]" : "bg-[#F2F2F2] text-[#5C6B7A]"}`}
                    >
                      <Power className="h-3 w-3" /> {b.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing({ ...b })} data-testid={`benefit-edit-${b.benefit_id}`} className="p-1.5 rounded-lg hover:bg-[#EAF1FA]">
                      <Pencil className="h-4 w-4 text-[#1E4F87]" />
                    </button>
                    <button onClick={() => remove(b)} data-testid={`benefit-delete-${b.benefit_id}`} className="p-1.5 rounded-lg hover:bg-[#FCE9E6] ml-1">
                      <Trash2 className="h-4 w-4 text-[#A53A2A]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <BenefitForm
          value={editing}
          plans={plans}
          catalog={catalog}
          saving={saving}
          onChange={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function BenefitForm({ value, plans, catalog, saving, onChange, onSave, onClose }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="benefit-form-modal">
        <div className="sticky top-0 bg-white border-b border-[#E1E8F0] px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[#0F2540]">
            {value.benefit_id ? "Edit Benefit" : "New Benefit"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F2F2F2]"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Title + description */}
          <div>
            <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Title *</label>
            <input
              type="text"
              value={value.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid="benefit-form-title"
              className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none"
              placeholder="e.g. Free OPD Consultation"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Description</label>
            <textarea
              value={value.description || ""}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none"
              placeholder="Short description shown in member wallet"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Category</label>
              <select value={value.category} onChange={(e) => set("category", e.target.value)} data-testid="benefit-form-category"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.benefit_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Type</label>
              <select value={value.benefit_type} onChange={(e) => set("benefit_type", e.target.value)} data-testid="benefit-form-type"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.benefit_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
              </select>
            </div>
          </div>

          {value.benefit_type === "discount" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Discount Type</label>
                <select value={value.discount_type} onChange={(e) => set("discount_type", e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                  <option value="percent">Percentage (%)</option>
                  <option value="amount">Fixed Amount (₹)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">
                  Value {value.discount_type === "percent" ? "(%)" : "(₹)"}
                </label>
                <input type="number" min="0" value={value.discount_value} onChange={(e) => set("discount_value", e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Usage Limit</label>
              <input type="number" min="0" value={value.usage_limit ?? ""} onChange={(e) => set("usage_limit", e.target.value)}
                placeholder="leave blank for unlimited"
                data-testid="benefit-form-usage-limit"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
              <div className="text-xs text-[#5C6B7A] mt-1">Per reset period. Blank = unlimited.</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Reset Period</label>
              <select value={value.reset_period} onChange={(e) => set("reset_period", e.target.value)} data-testid="benefit-form-reset"
                className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none">
                {(catalog.reset_periods || []).map((p) => <option key={p} value={p}>{PERIOD_LABEL[p] || p}</option>)}
              </select>
            </div>
          </div>

          {/* Plan assignment */}
          <div>
            <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Attach to Plans</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {plans.map((p) => {
                const checked = (value.plan_ids || []).includes(p.plan_id);
                return (
                  <label key={p.plan_id} className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border ${checked ? "bg-[#1E4F87] text-white border-[#1E4F87]" : "bg-white text-[#0F2540] border-[#E1E8F0] hover:border-[#1E4F87]"}`}>
                    <input type="checkbox" className="hidden"
                      data-testid={`benefit-form-plan-${p.plan_id}`}
                      checked={checked}
                      onChange={() => {
                        const cur = new Set(value.plan_ids || []);
                        if (cur.has(p.plan_id)) cur.delete(p.plan_id); else cur.add(p.plan_id);
                        set("plan_ids", Array.from(cur));
                      }} />
                    {p.name}
                  </label>
                );
              })}
              {plans.length === 0 && <span className="text-xs text-[#5C6B7A]">No plans available</span>}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#5C6B7A] uppercase tracking-wider">Departments (info only)</label>
            <input type="text"
              value={(value.department_applicability || []).join(", ")}
              onChange={(e) => set("department_applicability", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              placeholder="e.g. OPD, Pathology, Radiology"
              className="mt-1 w-full px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!value.active} onChange={(e) => set("active", e.target.checked)}
              data-testid="benefit-form-active" className="h-4 w-4 accent-[#1E4F87]" />
            <span className="text-sm text-[#0F2540]">Active (members can use this benefit)</span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E1E8F0] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E1E8F0] rounded-xl hover:border-[#1E4F87]">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="benefit-form-save"
            className="px-4 py-2 text-sm bg-[#1E4F87] text-white rounded-xl font-semibold hover:bg-[#0F2540] disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save benefit
          </button>
        </div>
      </div>
    </div>
  );
}
